import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "react-query";
import { PageBody } from "../../components/PageBody";
import { PageTitle } from "../../components/Headers";
import { getSEOForPath } from "../../config/seo";
import { getSteamRefreshStatus } from "../../api/steam";
import { ActivityChart } from "./insights/ActivityChart";
import { CommunityScatter } from "./insights/CommunityScatter";
import { CompletionOverTimeChart } from "./insights/CompletionOverTimeChart";
import { Card } from "./insights/InsightSection";
import { NextPlayList } from "./insights/NextPlayList";
import { UnplayedTable } from "./insights/UnplayedTable";
import { HowThisIsBuilt, Limitations, ReadingTheNumbers, WhyNoSnapshots } from "./insights/InsightsEducation";
import { useActivitySeries, useCompletionSeries, useUnplayed } from "./insights/useSteamInsights";

const formatRefreshed = (iso) =>
    new Date(iso).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const lastPoint = (series) => (series && series.length ? series[series.length - 1] : null);

const Stat = ({ label, value, note }) => (
    <Card className="text-center">
        <div className="text-sm text-gray-500">{label}</div>
        <div className="mt-1 text-3xl font-bold text-gray-800">{value ?? "–"}</div>
        {note && <div className="mt-1 text-sm text-gray-500">{note}</div>}
    </Card>
);

const HeadlineStats = () => {
    const completion = useCompletionSeries("steam_average", "year");
    const activity = useActivitySeries("year");
    const unplayed = useUnplayed();

    const latestCompletion = lastPoint(completion.data?.series);
    const latestActivity = lastPoint(activity.data?.series);

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat
                label="Steam average"
                value={latestCompletion?.value != null ? `${latestCompletion.value.toFixed(1)}%` : null}
                note={latestCompletion ? `Across ${latestCompletion.games} games with an unlock` : null}
            />
            <Stat
                label="Games started"
                value={latestActivity?.cum_games_started?.toLocaleString("en-GB")}
                note={latestActivity ? `${latestActivity.cum_games_completed} completed` : null}
            />
            <Stat
                label="Never played"
                value={unplayed.data?.length?.toLocaleString("en-GB")}
                note="Library games with no playtime"
            />
        </div>
    );
};

export const SteamInsights = () => {
    const location = useLocation();
    const seo = getSEOForPath(location.pathname);

    const { data: refreshStatus } = useQuery(
        "steamRefreshStatus",
        () => getSteamRefreshStatus().then((response) => response.data),
        { staleTime: 5 * 60 * 1000 }
    );
    const completionMeta = useCompletionSeries("steam_average", "year").data?.meta;
    const lastRefreshedAt = refreshStatus?.last_refreshed_at;

    return (
        <PageBody>
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                {seo.keywords && <meta name="keywords" content={seo.keywords} />}
            </Helmet>

            <PageTitle
                title="Steam Insights"
                backLink="/steam/games"
                subtitle={lastRefreshedAt ? `Library data refreshed ${formatRefreshed(lastRefreshedAt)}` : false}
            />

            <div className="max-w-prose space-y-3 text-base leading-relaxed text-gray-700">
                <p>
                    What should I play next? Which games have I never touched? How has my achievement completion
                    changed over the years — and which games have achievements most players pick up?
                </p>
                <p>
                    Steam only reports the present, so this page rebuilds the past from dated events: achievement
                    unlocks, last-played times and what the daily refresh observes. The{" "}
                    <Link
                        to="/steam/games"
                        className="font-semibold text-gray-800 underline decoration-gray-300 underline-offset-4 hover:text-gray-900"
                    >
                        library page
                    </Link>{" "}
                    lists every game; this one looks for patterns.
                </p>
            </div>

            <div className="mt-8">
                <HeadlineStats />
            </div>

            <CompletionOverTimeChart />
            <ActivityChart />
            <NextPlayList />
            <UnplayedTable />
            <CommunityScatter />

            <HowThisIsBuilt />
            <WhyNoSnapshots />
            <ReadingTheNumbers />
            <Limitations meta={completionMeta} />
        </PageBody>
    );
};
